import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import getScrollParent from '../utils/getScrollParent';

interface InfiniteScrollSentinelProps {
  /** Loads the next page. Called when the sentinel nears the bottom. */
  onLoadMore: () => void;
  /** True while a page is loading (prevents duplicate loads). */
  loading: boolean;
  /** Whether there is another page to load. */
  hasMore: boolean;
  /** How far (px) from the bottom to start loading. */
  offset?: number;
  /**
   * Renders one placeholder card. When provided, loading shows skeleton cards
   * (sized to the posts actually remaining) instead of the spinner.
   */
  renderSkeleton?: (index: number) => ReactNode;
  /** Total items on the server (from the paginated response) — sizes the last page's skeletons. */
  totalCount?: number;
  /** Items already rendered in the list. */
  loadedCount?: number;
  /** Items per page. */
  pageSize?: number;
}

/** How many placeholders to show: the posts left on the server, capped at one page. */
const skeletonCount = (totalCount: number | undefined, loadedCount: number | undefined, pageSize: number): number => {
  if (typeof totalCount !== 'number' || typeof loadedCount !== 'number') return pageSize;
  return Math.max(1, Math.min(pageSize, totalCount - loadedCount));
};

/**
 * Replaces the "Load more" button with infinite scroll. A rAF-throttled scroll
 * listener on the actual scroll container triggers `onLoadMore` when the sentinel
 * comes within `offset` of the bottom.
 *
 * The sentinel element is always rendered (collapsed when there's nothing more)
 * so the listener binds once and stays — binding conditionally on `hasMore`
 * missed the container when the first render had no next page yet, which is why
 * it previously only kicked in after navigating away and back.
 */
const InfiniteScrollSentinel: FC<InfiniteScrollSentinelProps> = ({
  onLoadMore,
  loading,
  hasMore,
  offset = 600,
  renderSkeleton,
  totalCount,
  loadedCount,
  pageSize = 10,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [nearBottom, setNearBottom] = useState(false);
  // Keep the latest callback without re-subscribing the listener.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const scrollParent = getScrollParent(el);
    const target: HTMLElement | Window = scrollParent ?? window;

    let ticking = false;
    const check = () => {
      ticking = false;
      const sentinelTop = el.getBoundingClientRect().top;
      const viewportBottom = scrollParent
        ? scrollParent.getBoundingClientRect().bottom
        : window.innerHeight;
      setNearBottom(sentinelTop - viewportBottom <= offset);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(check);
      }
    };

    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check(); // evaluate initial position (e.g. short lists already at the bottom)

    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [offset]);

  // Fire whenever we're near the bottom and idle — re-fires as loading clears to
  // keep paginating while the sentinel stays in range.
  useEffect(() => {
    if (nearBottom && hasMore && !loading) {
      onLoadMoreRef.current();
    }
  }, [nearBottom, hasMore, loading]);

  const showSkeletons = Boolean(renderSkeleton) && hasMore && loading;

  return (
    <>
      {showSkeletons &&
        Array.from({ length: skeletonCount(totalCount, loadedCount, pageSize) }, (_, i) => (
          <React.Fragment key={i}>{renderSkeleton!(i)}</React.Fragment>
        ))}
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: hasMore && !renderSkeleton ? 'var(--space-stack-relaxed)' : 0,
          minHeight: hasMore && !renderSkeleton ? 48 : 0,
        }}
      >
        {hasMore && loading && !renderSkeleton && <CircularProgress size={24} color="primary" />}
      </Box>
    </>
  );
};

export default InfiniteScrollSentinel;
